import { MODEL_REGISTRY, getImageGenerationModels, getVideoModels } from '../../config/modelConfig';

const IMAGE_INTENT =
  /\b(generate|create|make|draw|render|design|show|need)\b.{0,40}\b(image|picture|photo|illustration|logo|icon|hero|artwork|thumbnail)\b|\b(image|picture|photo|illustration) of\b|\bdall-?e\b|\bimagen\b|\bnano-?banana\b/i;

const FENCE_PATH =
  /```([a-zA-Z0-9_+.-]+)?[^\n]*?\b(?:path|file|filename)\s*=\s*["']?([^\s"'`]+)["']?[^\n]*\n([\s\S]*?)```/g;
const FENCE_FILE_COMMENT =
  /```([a-zA-Z0-9_+.-]+)?\s*\n(?:<!--\s*FILE:\s*([^\s]+)\s*-->|#\s*FILE:\s*([^\s]+)|\/\/\s*FILE:\s*([^\s]+))\s*\n([\s\S]*?)```/gi;
const EQUALS = /===\s*FILE:\s*(.+?)\s*===\s*\n([\s\S]*?)===\s*END FILE\s*===/gi;
const FENCE_LANG =
  /```(html|htm|css|javascript|js|jsx|typescript|ts|tsx|json|markdown|md|svg|text|python|py)(?![^\n]*\b(?:path|file|filename)\s*=)[^\n]*\n([\s\S]*?)```/gi;
const HTML_DOC = /<!DOCTYPE html\b[\s\S]*?<\/html\s*>|<html\b[\s\S]*?<\/html\s*>/i;

const LANG_PATH: Record<string, string> = {
  html: 'index.html',
  htm: 'index.html',
  css: 'styles.css',
  javascript: 'app.js',
  js: 'app.js',
  jsx: 'app.jsx',
  typescript: 'app.ts',
  ts: 'app.ts',
  tsx: 'app.tsx',
  json: 'package.json',
  markdown: 'README.md',
  md: 'README.md',
  svg: 'assets/icon.svg',
  text: 'notes.txt',
  python: 'clients/chain.py',
  py: 'clients/chain.py',
};

export type ExtractedFile = { path: string; content: string; language: string };

export type ImageModelChoice = {
  apiName: string;
  displayName: string;
  provider: string;
  intelligenceRating?: number;
};

export function isImageGenModel(model?: string | null): boolean {
  if (!model) return false;
  return MODEL_REGISTRY[model]?.category === 'image';
}

export function modelCanSeeImages(model?: string | null): boolean {
  if (!model) return false;
  return Boolean(MODEL_REGISTRY[model]?.supportsVision);
}

export function suggestedImageModel(): string {
  return suggestedImageModels()[0]?.apiName || 'nano-banana-2';
}

/** Best image-output models across providers (not vision-only text models). */
export function suggestedImageModels(): ImageModelChoice[] {
  const preferred = [
    'nano-banana-2',
    'nano-banana-pro',
    'gpt-image-2',
    'nano-banana-2-lite',
  ];
  const seen = new Set<string>();
  const out: ImageModelChoice[] = [];
  const selectable = getImageGenerationModels();
  const byName = Object.fromEntries(selectable.map((m) => [m.apiName, m]));

  for (const id of preferred) {
    const m = byName[id];
    if (!m || seen.has(m.apiName)) continue;
    seen.add(m.apiName);
    out.push({
      apiName: m.apiName,
      displayName: m.displayName,
      provider: m.provider,
      intelligenceRating: m.intelligenceRating,
    });
  }

  const rest = selectable
    .filter((m) => !seen.has(m.apiName))
    .sort((a, b) => (b.intelligenceRating ?? 0) - (a.intelligenceRating ?? 0));
  for (const m of rest) {
    if (out.length >= 6) break;
    if (out.some((x) => x.provider === m.provider)) continue;
    seen.add(m.apiName);
    out.push({
      apiName: m.apiName,
      displayName: m.displayName,
      provider: m.provider,
      intelligenceRating: m.intelligenceRating,
    });
  }
  return out.slice(0, 6);
}

export function allImageGenModels(): ImageModelChoice[] {
  const preferred = suggestedImageModels();
  const seen = new Set(preferred.map((m) => m.apiName));
  const rest = getImageGenerationModels()
    .filter((m) => !seen.has(m.apiName))
    .sort((a, b) => (b.intelligenceRating ?? 0) - (a.intelligenceRating ?? 0))
    .map((m) => ({
      apiName: m.apiName,
      displayName: m.displayName,
      provider: m.provider,
      intelligenceRating: m.intelligenceRating,
    }));
  return [...preferred, ...rest];
}

export function suggestedVideoModel(): string {
  return allVideoGenModels()[0]?.apiName || 'veo-3.1-fast-generate-001';
}

export function allVideoGenModels(): ImageModelChoice[] {
  const preferred = [
    'veo-3.1-fast-generate-001',
    'veo-3.1-fast',
    'veo-3.1-generate-001',
    'veo-3.1',
  ];
  const selectable = getVideoModels();
  const byName = Object.fromEntries(selectable.map((m) => [m.apiName, m]));
  const seenLlm = new Set<string>();
  const out: ImageModelChoice[] = [];
  const push = (m: (typeof selectable)[0]) => {
    const llm = m.llmName || m.apiName;
    if (seenLlm.has(llm)) return;
    seenLlm.add(llm);
    out.push({
      apiName: m.apiName,
      displayName: m.displayName,
      provider: m.provider,
      intelligenceRating: m.intelligenceRating,
    });
  };
  for (const id of preferred) {
    const m = byName[id];
    if (m) push(m);
  }
  selectable
    .slice()
    .sort((a, b) => (b.intelligenceRating ?? 0) - (a.intelligenceRating ?? 0))
    .forEach(push);
  return out;
}

export function looksLikeImageRequest(text: string): boolean {
  return IMAGE_INTENT.test((text || '').trim());
}

export function formatShortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Today / yesterday / Sep 1, 4:11 PM — for picking among duplicate chain names. */
export function formatWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startToday - startThat) / 86400000);
  if (dayDiff === 0) return `today, ${time}`;
  if (dayDiff === 1) return `yesterday, ${time}`;
  const day = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
  return `${day}, ${time}`;
}

export function byUpdatedDesc<T extends { updated_at?: string | null }>(a: T, b: T): number {
  return new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime();
}

export function looksLikeNetworkDrop(err: unknown): boolean {
  const msg = String((err as any)?.message || err || '');
  return /network error|failed to fetch|load failed|abort|err_incomplete|body stream/i.test(msg);
}

export async function readSseResponse(res: Response, onEvent: (event: any) => void): Promise<void> {
  if (!res.body) throw new Error('No stream');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) parseSseBlock(buffer, onEvent);
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';
      for (const block of blocks) parseSseBlock(block, onEvent);
    }
  } catch (e) {
    // Proxies and SSE often throw "network error" / "body stream" when the
    // server closes a finished stream. Flush any trailing block and treat
    // that as a clean end so the UI does not scream "connection dropped".
    if (buffer.trim()) parseSseBlock(buffer, onEvent);
    if (looksLikeNetworkDrop(e)) return;
    throw e;
  }
}

export function parseSseBlock(
  block: string,
  onEvent: (event: any) => void,
): void {
  for (const line of block.split('\n')) {
    if (!line.startsWith('data:')) continue;
    const raw = line.slice(5).trim();
    if (!raw) continue;
    try {
      onEvent(JSON.parse(raw));
    } catch {
      // ignore partial JSON
    }
  }
}

function cleanPath(raw: string): string {
  let path = (raw || '').trim().replace(/^\.\//, '').replace(/\\/g, '/');
  while (path.startsWith('/')) path = path.slice(1);
  path = path
    .split('/')
    .filter((p) => p && p !== '..')
    .join('/');
  return path.slice(0, 180) || 'untitled.txt';
}

function langFor(path: string, fenceLang?: string | null): string {
  const p = path.toLowerCase();
  if (p.endsWith('.html') || p.endsWith('.htm')) return 'html';
  if (p.endsWith('.css')) return 'css';
  if (p.endsWith('.js') || p.endsWith('.mjs')) return 'javascript';
  if (p.endsWith('.md')) return 'markdown';
  return (fenceLang || 'text').toLowerCase();
}

function stripFenceNoise(chunk: string): string {
  return (chunk || '').replace(/^```[a-zA-Z0-9_+.-]*\s*/, '').replace(/```$/, '').trim();
}

/** Client-side twin of api/core/app_builder/parse_files.py */
export function extractProjectFilesLite(text: string): ExtractedFile[] {
  if (!text) return [];
  const found: Record<string, ExtractedFile> = {};
  const order: string[] = [];

  const add = (rawPath: string, content: string, fenceLang?: string | null) => {
    const path = cleanPath(rawPath);
    const body = (content || '').replace(/^\n|\n$/g, '');
    if (!path || !body) return;
    if (!found[path]) order.push(path);
    found[path] = { path, content: body, language: langFor(path, fenceLang) };
  };

  let m: RegExpExecArray | null;
  const pathRe = new RegExp(FENCE_PATH.source, 'g');
  while ((m = pathRe.exec(text))) add(m[2], m[3], m[1]);

  const fileRe = new RegExp(FENCE_FILE_COMMENT.source, 'gi');
  while ((m = fileRe.exec(text))) add(m[2] || m[3] || m[4], m[5], m[1]);

  const eqRe = new RegExp(EQUALS.source, 'gi');
  while ((m = eqRe.exec(text))) add(m[1], m[2], null);

  const langRe = new RegExp(FENCE_LANG.source, 'gi');
  while ((m = langRe.exec(text))) {
    const lang = (m[1] || '').toLowerCase();
    const content = m[2] || '';
    if (/^\s*(?:<!--\s*FILE:|#\s*FILE:|\/\/\s*FILE:)/.test(content)) continue;
    let path = LANG_PATH[lang] || 'untitled.txt';
    if ((lang === 'javascript' || lang === 'js') && content.includes('PASSIO_CONFIG') && !content.slice(0, 240).includes('function ')) {
      path = 'passio.config.example.js';
    }
    if (!found[path]) add(path, content, lang);
  }

  const hasHtml = order.some((p) => p.endsWith('.html') || p.endsWith('.htm'));
  if (!hasHtml) {
    const htmlM = HTML_DOC.exec(text);
    if (htmlM) {
      add('index.html', htmlM[0], 'html');
      let rest = text.slice(htmlM.index + htmlM[0].length);
      const readmeM = rest.match(/(?:^|\n)(#{1,2} [^\n]{4,}[\s\S]*)/);
      let body = rest;
      if (readmeM && readmeM.index != null) {
        const idx = readmeM[0].startsWith('\n') ? readmeM.index + 1 : readmeM.index;
        body = rest.slice(0, readmeM.index);
        add('README.md', rest.slice(idx).trim(), 'markdown');
      }
      const jsM = body.match(
        /(?:^|\n)(?=window\.PASSIO_CONFIG|document\.addEventListener|async function |function |const |let |var )/,
      );
      const splitAt = jsM && jsM.index != null ? jsM.index : -1;
      const cssRaw = splitAt >= 0 ? body.slice(0, splitAt) : body;
      const jsRaw = splitAt >= 0 ? body.slice(splitAt) : '';
      const css = stripFenceNoise(cssRaw);
      const js = stripFenceNoise(jsRaw);
      if (css && (css.includes('{') || css.trim().startsWith('/*'))) add('styles.css', css, 'css');
      if (js && (js.includes('function') || js.includes('PASSIO_CONFIG') || js.includes('addEventListener'))) {
        add('app.js', js, 'javascript');
      }
    }
  }

  return order.map((p) => found[p]).filter(Boolean);
}

/** True when a builder reply looks like code but extract found no project files. */
export function builderReplyMissedCanvas(text: string): boolean {
  if (!text || extractProjectFilesLite(text).length) return false;
  return /```/.test(text) || /<!DOCTYPE html\b|<html[\s>]/i.test(text);
}

export type CachedPlaygroundMsg = {
  id: string;
  role: string;
  content: string;
  attachments?: unknown;
  artifacts?: unknown;
  model_used?: string | null;
  usage?: { credits_used?: number } | null;
  created_at?: string;
};

const LS_PREFIX = 'easyai.playground.msgs.';

export function formatCredits(n?: number | null): string {
  if (n == null || Number.isNaN(Number(n))) return '';
  const v = Number(n);
  if (v === 0) return '0 cr';
  if (v < 0.01) return `${v.toFixed(4)} cr`;
  return `${v.toFixed(2)} cr`;
}

export function cachePlaygroundMessages(sessionId: string, messages: CachedPlaygroundMsg[]): void {
  if (!sessionId || typeof localStorage === 'undefined') return;
  try {
    const slim = messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content || '',
      attachments: m.attachments || null,
      artifacts: m.artifacts || null,
      model_used: m.model_used || null,
      usage: m.usage || null,
      created_at: m.created_at,
    }));
    localStorage.setItem(LS_PREFIX + sessionId, JSON.stringify(slim));
  } catch {
    // quota
  }
}

export function mergePlaygroundMessages<T extends CachedPlaygroundMsg>(sessionId: string, apiMessages: T[]): T[] {
  const hydrated = (apiMessages || []).map((m) => {
    if ((m.content || '').trim()) return m;
    const body = (m as any).settings_snapshot?.body;
    if (typeof body === 'string' && body.trim()) return { ...m, content: body };
    return m;
  });
  let local: CachedPlaygroundMsg[] = [];
  try {
    local = JSON.parse(localStorage.getItem(LS_PREFIX + sessionId) || '[]');
  } catch {
    local = [];
  }
  if (!local.length) return hydrated;
  const byId = new Map(local.map((m) => [m.id, m]));
  const used = new Set<string>();
  const merged = hydrated.map((m) => {
    if ((m.content || '').trim()) return m;
    const loc = byId.get(m.id);
    if (loc && (loc.content || '').trim()) {
      used.add(loc.id);
      return {
        ...m,
        content: loc.content,
        artifacts: (m.artifacts as any)?.length ? m.artifacts : loc.artifacts,
        usage: m.usage || loc.usage,
      };
    }
    if (m.role === 'assistant') {
      const fallback = local.find(
        (x) => x.role === 'assistant' && !used.has(x.id) && (x.content || '').trim(),
      );
      if (fallback) {
        used.add(fallback.id);
        return {
          ...m,
          content: fallback.content,
          artifacts: (m.artifacts as any)?.length ? m.artifacts : fallback.artifacts,
          usage: m.usage || fallback.usage,
        };
      }
    }
    return m;
  });
  return merged;
}
